import { StatusEnum } from "@/api/lctApISchemas";
import { useAuth } from "@/auth/hooks/useAuth";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ApplicationStatus } from "@/utils/constants";
import { CircleHelp, CircleCheck, Clock4, Info, TriangleAlert, LockIcon } from "lucide-react";

export type StatusObject = {
  bgColor: string;
  textColor: string;
  icon: React.ElementType;
  text: string | ((totalParts?: number) => string);
};

export const statusDetails = new Map<StatusEnum, StatusObject>([
  [ApplicationStatus.STARTED, { bgColor: "bg-blue-100", textColor: "text-blue-900", icon: Clock4, text: "In Progress" }],
  [
    ApplicationStatus.PART_1_SUBMITTED,
    {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-900",
      icon: Info,
      text: (totalParts?: number) => (Number(totalParts) > 1 ? "Awaiting review (Part 1)" : "Awaiting review"),
    },
  ],
  [
    ApplicationStatus.PART_1_APPROVED,
    { bgColor: "bg-primaryAccent", textColor: "text-green-900", icon: CircleCheck, text: "Part 1 Approved" },
  ],
  [
    ApplicationStatus.PART_2_SUBMITTED,
    { bgColor: "bg-yellow-100", textColor: "text-yellow-900", icon: Info, text: "Awaiting review (Part 2)" },
  ],
  [ApplicationStatus.APPROVED, { bgColor: "bg-primaryAccent", textColor: "text-green-900", icon: CircleCheck, text: "Approved" }],
  [ApplicationStatus.REJECTED, { bgColor: "bg-red-50", textColor: "text-error", icon: TriangleAlert, text: "Rejected" }],
  [
    ApplicationStatus.FURTHER_INFO_PART_1,
    { bgColor: "bg-yellow-100", textColor: "text-yellow-900", icon: CircleHelp, text: "Updates required (part 1)" },
  ],
  [
    ApplicationStatus.FURTHER_INFO_PART_2,
    { bgColor: "bg-yellow-100", textColor: "text-yellow-900", icon: CircleHelp, text: "Updates required (part 2)" },
  ],
]);

const defaultDetails: StatusObject = {
  bgColor: "bg-gray-100",
  textColor: "#374151",
  icon: CircleHelp,
  text: "NA",
};

const lockedDetails: StatusObject = {
  bgColor: "bg-gray-600",
  textColor: "text-white",
  icon: LockIcon,
  text: "Locked",
};

interface StatusPillProps {
  applicationStatus?: StatusEnum;
  onClick?: () => void;
  statusDetail?: StatusObject;
  totalParts?: number;
  isLocked?: boolean;
}

export const StatusPill = ({ applicationStatus, totalParts, onClick, statusDetail, isLocked }: StatusPillProps) => {
  const details = isLocked
    ? lockedDetails
    : (statusDetail ?? (applicationStatus && statusDetails.get(applicationStatus)) ?? defaultDetails);
  const text = typeof details.text === "function" ? details.text(totalParts) : details.text;
  const { isStaff } = useAuth();

  const getTooltipText = () => {
    if (isLocked) {
      return "Part 2 is locked until Part 1 is approved";
    }

    const tooltipTexts = {
      [ApplicationStatus.STARTED]: {
        user: "Application in progress - complete all required fields",
        staff: "Application in progress",
      },
      [ApplicationStatus.PART_1_SUBMITTED]: {
        user: "Part 1 is under review",
        staff: "Part 1 is ready for review",
      },
      [ApplicationStatus.PART_1_APPROVED]: {
        user: "Part 1 approved - you can now complete Part 2",
        staff: "Part 1 approved - awaiting Part 2 submission",
      },
      [ApplicationStatus.PART_2_SUBMITTED]: {
        user: "Part 2 is under review",
        staff: "Part 2 is ready for review",
      },
      [ApplicationStatus.APPROVED]: "Application has been approved",
      [ApplicationStatus.REJECTED]: "Application has been rejected",
      [ApplicationStatus.FURTHER_INFO_PART_1]: {
        user: "Updates required on part 1 - please review comments",
        staff: "Updates required on part 1",
      },
      [ApplicationStatus.FURTHER_INFO_PART_2]: {
        user: "Updates required on part 2 - please review comments",
        staff: "Updates required on part 2",
      },
    };

    const text = tooltipTexts[applicationStatus as keyof typeof tooltipTexts];
    if (!text) return "";

    return typeof text === "string" ? text : isStaff ? text.staff : text.user;
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div
            className={`flex w-fit items-center gap-2 rounded-full px-4 py-2 ${details.bgColor} ${onClick ? "cursor-pointer" : "cursor-help"}`}
            onClick={!isLocked || onClick ? onClick : undefined}
          >
            <details.icon className={`h-4 w-4 ${details.textColor}`} />
            <span className={`text-sm font-medium ${details.textColor ?? "text-slate-800"}`}>{text}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={5}>
          {getTooltipText()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
